/**
 * =============================================================================
 * RFI MANAGEMENT PANEL COMPONENT
 * =============================================================================
 *
 * Staff-facing "Need-More-Info" (RFI) panel — the admin surface over the
 * already-shipped RFI backend (PR A–F). It mounts in the question-detail modal
 * beside the reply-moderation panel and runs the whole RFI workflow: open an
 * RFI, link a customer's approved reply as evidence, review each item, and see
 * the request roll up after each mutation.
 *
 * Self-contained + RELOCATABLE (the intended phase-2 "Need-More-Info" workbench
 * panel, BL-ADMIN-WORKBENCH-IA-001): it depends ONLY on
 * `{ questionId, questionStatus, token, testId }` and owns its own data hooks,
 * so the IA lift moves it by changing one import + one render line.
 *
 * It is mutation/refetch-driven, NOT live-polling: every mutation reconciles
 * from its response (merge by `rfiId`), and an explicit "Refresh RFIs" control
 * pulls changes made in another session. Cross-panel question-status refresh is
 * intentionally NOT wired (D-3) — status transitions surface via toast + a
 * persistent inline notice instead.
 *
 * PR G — BL-ADMIN-RFI-PANEL-001.
 *
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import type { AdminQuestionListItem, AdminRfiRequest } from '@find-my-part/types';
import { useRfi } from '../../../hooks/useRfi';
import { useRfiCandidateReplies } from '../../../hooks/useRfiCandidateReplies';
import { useMediaAuthoring } from '../../../hooks/useMediaAuthoring';
import { AttachImagesTray } from '../../Media/AttachImagesTray';
import { RfiCreateForm } from './RfiCreateForm';
import { RfiRequestCard } from './RfiRequestCard';
import { resolveStatusNotice } from './statusNotice';
import { tokens } from '../../../tokens';
import { EmptyState } from '../../common/EmptyState';

/**
 * Props for RfiManagementPanel. This is the entire relocation contract — keep
 * it to these four fields (D-3 / §5 modal-close caveat).
 */
export interface RfiManagementPanelProps {
  /** Question ID to manage RFIs for. */
  questionId: string;
  /** Current question status — initial UX gate for the create form (D-1). */
  questionStatus: AdminQuestionListItem['status'];
  /** Auth token for API calls. */
  token: string;
  /** Optional test ID prefix. */
  testId?: string;
}

/** Simple skeleton card for the RFI list loading state. */
function RfiSkeleton(): React.ReactElement {
  return (
    <Box
      padding="medium"
      marginBottom="small"
      style={{ border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.dimension.radius.lg }}
    >
      <style>
        {`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}
      </style>
      <Box
        style={{
          width: '40%',
          height: '16px',
          backgroundColor: tokens.color.surface.skeleton,
          borderRadius: tokens.dimension.radius.sm,
          marginBottom: tokens.dimension.space.sm,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <Box
        style={{
          width: '100%',
          height: '16px',
          backgroundColor: tokens.color.surface.skeleton,
          borderRadius: tokens.dimension.radius.sm,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
    </Box>
  );
}

/**
 * The RFI management panel.
 */
export function RfiManagementPanel({
  questionId,
  questionStatus,
  token,
  testId,
}: RfiManagementPanelProps): React.ReactElement {
  // PR4b (ADR-022 §10(A)): the anchor of the RFI just opened here — RFI
  // request images anchor to the RFI's own staff-authored anchor reply, so
  // the attach tray appears only right after a create. Reset on dismiss and
  // on question/token change (a stale anchor must never cross questions).
  const [attachAnchor, setAttachAnchor] = useState<{ rfiId: string; replyId: string } | null>(
    null
  );
  const handleRfiCreated = useCallback((rfi: AdminRfiRequest): void => {
    setAttachAnchor({ rfiId: rfi.rfiId, replyId: rfi.replyId });
  }, []);

  const {
    rfis,
    isLoading,
    error,
    pendingActions,
    lastStatusTransition,
    refetch,
    createRfi,
    linkResponses,
    unlinkResponse,
    reviewItem,
    cancelRfi,
  } = useRfi({ questionId, token, onCreated: handleRfiCreated });

  const candidates = useRfiCandidateReplies({ questionId, token });

  const { uploadReplyImage } = useMediaAuthoring(token);

  // Per-file upload for the post-create attach tray, targeting the new RFI's
  // anchor reply (the internal replyId stays undisplayed — §2b).
  const handleAttachUpload = useCallback(
    async (file: File): Promise<{ success: boolean; message?: string }> => {
      if (!attachAnchor) return { success: false, message: 'No RFI selected.' };
      return uploadReplyImage(attachAnchor.replyId, file);
    },
    [attachAnchor, uploadReplyImage]
  );

  // Which RFI's link picker is open (one at a time). Reset on question change so
  // a stale picker can't persist across questions (the hooks reset their own
  // caches separately). The attach anchor resets on the same boundary.
  const [linkPickerRfiId, setLinkPickerRfiId] = useState<string | null>(null);
  useEffect(() => {
    setLinkPickerRfiId(null);
    setAttachAnchor(null);
  }, [questionId, token]);

  // Picker close on a successful cancel is the PANEL's job (it owns
  // `linkPickerRfiId`), not the card's: wrap `cancelRfi` so an open picker for the
  // just-cancelled RFI is dismissed (the card flips read-only from the server RFI).
  const handleCancelRfi = useCallback(
    async (rfiId: string): Promise<boolean> => {
      const ok = await cancelRfi(rfiId);
      if (ok && linkPickerRfiId === rfiId) {
        setLinkPickerRfiId(null);
      }
      return ok;
    },
    [cancelRfi, linkPickerRfiId]
  );

  // Linked-response excerpt fallback: index the loaded candidate replies by id.
  const candidateRepliesById = useMemo(
    () => new Map(candidates.replies.map((reply) => [reply.replyId, reply])),
    [candidates.replies]
  );

  const notice = lastStatusTransition ? resolveStatusNotice(lastStatusTransition) : null;

  return (
    <Box data-testid={testId}>
      {/* Header */}
      <Flex alignItems="center" justifyContent="space-between" marginBottom="small">
        <FlexItem>
          <Text bold>
            Need more info (RFI) ({isLoading && rfis.length === 0 ? '…' : rfis.length})
          </Text>
        </FlexItem>
        <FlexItem>
          <Button
            variant="subtle"
            onClick={refetch}
            disabled={isLoading}
            data-testid={testId ? `${testId}-refresh` : undefined}
          >
            Refresh RFIs
          </Button>
        </FlexItem>
      </Flex>

      {/* Persistent inline status-transition notice (D-3). The modal passes a
          stale questionStatus snapshot, so this notice — not a transient toast —
          is the durable record of the transition until the question changes. */}
      {notice && (
        <Box
          padding="small"
          marginBottom="small"
          role="status"
          style={{
            border: `1px solid ${notice.borderColor}`,
            backgroundColor: notice.backgroundColor,
            borderRadius: tokens.dimension.radius.md,
          }}
          data-testid={testId ? `${testId}-status-notice` : undefined}
        >
          <Text>{notice.text}</Text>
        </Box>
      )}

      {/* Create form (status-gated, collapsible). Keyed by question/token so a
          half-written draft can NEVER ride along to a different question after
          a switch — the form remounts fresh (collapsed + empty) per question. */}
      <RfiCreateForm
        key={`${questionId}::${token}`}
        questionStatus={questionStatus}
        onCreate={createRfi}
        testId={testId ? `${testId}-create` : undefined}
      />

      {/* Post-create attach stage (PR4b — §10(A)): images for the RFI just
          opened, uploaded against its staff-authored anchor reply. Staff
          uploads are auto-approved server-side (shared tray — fold S3/A5). */}
      {attachAnchor && (
        <AttachImagesTray
          caption="Attach images to your RFI request"
          onDone={() => setAttachAnchor(null)}
          onUpload={handleAttachUpload}
          testId={testId ? `${testId}-attach` : undefined}
        />
      )}

      {/* Loading */}
      {isLoading && rfis.length === 0 && (
        <Box aria-busy="true" data-testid={testId ? `${testId}-loading` : undefined}>
          <RfiSkeleton />
          <RfiSkeleton />
        </Box>
      )}

      {/* Error */}
      {error && !isLoading && (
        <Box
          padding="large"
          style={{ textAlign: 'center' }}
          data-testid={testId ? `${testId}-error` : undefined}
        >
          <Text color="secondary">
            Failed to load RFIs
          </Text>
          <Button
            variant="secondary"
            onClick={refetch}
            data-testid={testId ? `${testId}-retry` : undefined}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Empty */}
      {!isLoading && !error && rfis.length === 0 && (
        <EmptyState
          variant="centered"
          testId={testId ? `${testId}-empty` : undefined}
          title="No RFIs yet"
          body="Open an RFI to request more information from the customer."
        />
      )}

      {/* List */}
      {!error && rfis.length > 0 && (
        <Box
          role="list"
          aria-label="RFI requests for this question"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {rfis.map((rfi) => (
            <RfiRequestCard
              key={rfi.rfiId}
              rfi={rfi}
              token={token}
              pendingActions={pendingActions}
              reviewItem={reviewItem}
              unlinkResponse={unlinkResponse}
              linkResponses={linkResponses}
              cancelRfi={handleCancelRfi}
              candidates={candidates}
              candidateRepliesById={candidateRepliesById}
              isPickerOpen={linkPickerRfiId === rfi.rfiId}
              onOpenPicker={setLinkPickerRfiId}
              onClosePicker={() => setLinkPickerRfiId(null)}
              testId={testId ? `${testId}-rfi-${rfi.rfiId}` : undefined}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
